import { TransferOperation, TransferStatus } from '@/constants';
import { AbstractModel } from '@/models/abstractModel';
import type { IPlayerSearchItem } from '@/types/player';
import type { ITransferApi } from '@/types/transfer';

export class TransferItem extends AbstractModel {
    public agentName: string | null = null;
    public amount: number | null = null;
    public approvedToolUserName: string | null = null;
    public createdAt: string = '';
    public currencyId: number | null = null;
    public description: string | null = null;
    public id: number = 0;
    public methodId: number | null = null;
    public operationId: number | null = null;
    public statusId: number | null = null;
    public toolUserId: number | null = null;
    public toolUserName: string = '';

    constructor(data?: ITransferApi) {
        super();

        if (!data) {
            return;
        }

        this.id = data.id;
        this.createdAt = data.created_at;
        this.operationId = data.operation_id;
        this.amount = data.amount != null ? Number(data.amount) : null;
        this.methodId = data.method_id;
        this.description = data.description;
        this.statusId = data.status_id;
        this.toolUserName = data.tool_user_name;
        this.toolUserId = data.tool_user_id;
        this.currencyId = data.currency_id;
        this.approvedToolUserName = data.approved_tool_user_name;
        this.agentName = data.agent_name;
    }

    static createEmpty(): TransferItem {
        return new TransferItem();
    }

    static operationClassFor(operationId: number | null): string {
        if (operationId == null) return '';

        switch (operationId) {
            case TransferOperation.Deposit:
                return 'text-tbGreen';
            case TransferOperation.Withdrawal:
                return 'text-orange';
            default:
                return '';
        }
    }

    static statusClassFor(statusId: number | null): string {
        if (statusId == null) return '';

        switch (statusId) {
            case TransferStatus.Approved:
                return 'text-tbGreen';
            case TransferStatus.Pending:
                return 'text-orange';
            case TransferStatus.Rejected:
            case TransferStatus.Expired:
                return 'text-tbRed';
            default:
                return '';
        }
    }

    get operationClass(): string {
        return TransferItem.operationClassFor(this.operationId);
    }

    get statusClass(): string {
        return TransferItem.statusClassFor(this.statusId);
    }

    toApiData(): Record<string, unknown> {
        const row: Record<string, unknown> = {
            operation_id: this.operationId,
            status_id: this.statusId,
            tool_user_id: this.toolUserId,
            method_id: this.methodId,
            amount: this.amount,
            description: this.description?.trim() || null,
            approved_tool_user_id: 0,
            created_at: null,
            updated_at: null,
        };

        if (this.id > 0) {
            row.id = this.id;
        }

        return row;
    }

    toPlayerSearchItem(): IPlayerSearchItem | null {
        if (!this.toolUserId) return null;

        return {
            id: this.toolUserId,
            login: this.toolUserName,
            name: this.toolUserName,
            platform_id: 0,
            currency_id: this.currencyId ?? 0,
        };
    }
}
